import { AnimatePresence, motion } from 'framer-motion';
import { Info, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DealCountdown,
  ProductLogo,
  stockLabel,
  type ProductDto,
  type ProductVariant,
} from '@entities/product';
import { itemToText, usePlaceOrderMutation, type PlaceOrderResponse } from '@entities/order';
import { useGetMeQuery } from '@entities/user';
import { apiErrorCode, apiErrorMessage } from '@shared/api/baseApi';
import { SUPPORT_URL } from '@shared/config/support';
import { haptics } from '@shared/lib/telegram';
import { Button } from '@shared/ui/Button';
import { InsufficientBalanceSheet } from '@shared/ui/InsufficientBalanceSheet';
import styles from './ProductSheet.module.css';

interface ProductSheetProps {
  product: ProductDto | null;
  /** Base product title when several duration variants share this sheet. */
  title?: string | null;
  variants?: ProductVariant[];
  onVariantSelect?: (productId: string) => void;
  onClose: () => void;
  /** Opens straight on the confirmation step, for the grid's Buy button. The
   *  step itself is never skipped: it is where the agreement is given and any
   *  detail the order needs is asked for. */
  startConfirming?: boolean;
  clockOffsetMs?: number;
}

/**
 * The number, right after it was rented.
 *
 * Two things the buyer needs before they leave this screen: the number itself,
 * and how long they have to use it. The code that follows is not shown here —
 * it lands in Orders, which is the one screen that polls for it, so there is a
 * single place a code is ever delivered rather than two that can disagree.
 */
function NumberReceipt({
  phoneNumber,
  expiresAt,
  onCopy,
}: {
  phoneNumber: string;
  expiresAt: string;
  onCopy: (text: string) => Promise<void> | void;
}) {
  const [remainingMs, setRemainingMs] = useState(() => Date.parse(expiresAt) - Date.now());

  // Recomputed from the absolute expiry rather than decremented, so a phone
  // that slept shows the truth when it wakes instead of a frozen countdown.
  useEffect(() => {
    const timer = setInterval(() => setRemainingMs(Date.parse(expiresAt) - Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return (
    <>
      <pre className={styles.receiptItem} onClick={() => void onCopy(phoneNumber)}>
        {phoneNumber}
      </pre>
      <p className={styles.receiptHint}>
        Tap to copy · enter it where you need the code
      </p>
      <p className={styles.receiptPending}>
        ⏳ {totalSeconds > 0
          ? `You have ${minutes}:${String(seconds).padStart(2, '0')} to use it. `
          : 'This number has expired. '}
        The code appears in Orders as soon as it arrives — if it never does, you are refunded
        automatically.
      </p>
    </>
  );
}

/** Bottom sheet holding product detail and the purchase confirmation. */
export function ProductSheet({
  product,
  title,
  variants = [],
  onVariantSelect,
  onClose,
  startConfirming = false,
  clockOffsetMs = 0,
}: ProductSheetProps) {
  const navigate = useNavigate();
  const { data: user } = useGetMeQuery();
  const [placeOrder, { isLoading }] = usePlaceOrderMutation();
  const [confirming, setConfirming] = useState(startConfirming);
  const [agreed, setAgreed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  // Set on success: the sheet becomes a receipt instead of closing, so the
  // delivered item and its steps are on screen the moment they are paid for.
  const [purchased, setPurchased] = useState<PlaceOrderResponse['order'] | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const lastPrice = useRef<{ productId: string; amount: string } | null>(null);
  // React state disables the button on the next render. This ref closes the
  // smaller same-frame gap so two rapid taps cannot dispatch two mutations.
  const submitting = useRef(false);
  const attemptId = useRef(crypto.randomUUID());
  const hasVariants = variants.length > 1;

  // A newly opened sheet must never inherit the previous product's state.
  useEffect(() => {
    setConfirming(startConfirming);
    setAgreed(false);
    setShowInstructions(false);
    setAnswer('');
    setError(null);
    setInsufficientBalance(false);
    setPurchased(null);
    submitting.current = false;
    attemptId.current = crypto.randomUUID();
  }, [product?.id, startConfirming]);

  // A deal can end while this sheet is open. Return to the product view so a
  // refreshed full price is never accepted by a tap intended for the old one.
  useEffect(() => {
    if (!product) {
      lastPrice.current = null;
      return;
    }
    const previous = lastPrice.current;
    if (
      !purchased &&
      previous?.productId === product.id &&
      previous.amount !== product.price.amount
    ) {
      setConfirming(false);
      setAgreed(false);
      setError('The deal ended or the price changed. Please review the current price.');
    }
    lastPrice.current = { productId: product.id, amount: product.price.amount };
  }, [product, purchased]);

  // All three views share this one element, so React keeps its scroll offset
  // across them: opening the Info panel, scrolling, then tapping Buy would
  // otherwise drop the customer into the middle of the agreement, below the
  // heading telling them what they are agreeing to.
  useEffect(() => {
    scroll.current?.scrollTo({ top: 0 });
  }, [confirming, purchased]);

  const affordable =
    user && product ? Number(user.balance.amount) >= Number(product.price.amount) : true;

  // A Buy tap from a product card opens this sheet directly on confirmation.
  // If the cached wallet already proves it cannot be paid for, show the same
  // deposit choice immediately instead of exposing a disabled low-balance CTA.
  //
  // A completed mutation refreshes `user`, so the expected post-purchase
  // deduction can also make `affordable` false. That refresh must not turn a
  // successful receipt into an insufficient-balance warning. Likewise, do not
  // second-guess an attempt while the server is still deciding its outcome.
  useEffect(() => {
    if (
      !startConfirming ||
      !product ||
      !user ||
      affordable ||
      purchased ||
      submitting.current
    ) return;
    setConfirming(false);
    setInsufficientBalance(true);
    haptics.notify('warning');
  }, [startConfirming, product, user, affordable, purchased]);

  const copyItem = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      haptics.notify('success');
    } catch {
      haptics.notify('error');
    }
  };

  /** Every buy affordance must enter the review/agreement step first. */
  const beginPurchaseReview = () => {
    setError(null);
    if (!affordable) {
      setInsufficientBalance(true);
      haptics.notify('warning');
      return;
    }
    setAgreed(false);
    setConfirming(true);
  };

  const buy = async () => {
    if (!product || submitting.current) return;

    if (!affordable) {
      setConfirming(false);
      setInsufficientBalance(true);
      haptics.notify('warning');
      return;
    }

    submitting.current = true;
    setError(null);
    try {
      const response = await placeOrder({
        productId: product.id,
        expectedPriceETB: product.price.amount,
        idempotencyKey: attemptId.current,
        ...(product.input ? { customerInput: answer.trim() } : {}),
      }).unwrap();
      haptics.notify('success');
      // The wallet invalidation may already have refreshed the balance by the
      // time this continuation runs. Success is authoritative for this
      // attempt, so clear any stale balance sheet before showing its receipt.
      setInsufficientBalance(false);
      setPurchased(response.order);
    } catch (cause) {
      const code = apiErrorCode(cause);
      // A named refusal means the server conclusively handled this attempt;
      // another deliberate tap may use a fresh ID. On a network failure or
      // generic 500 the outcome is unknown, so retain the key for safe replay.
      if (code && code !== 'internal_error') attemptId.current = crypto.randomUUID();
      if (code === 'INSUFFICIENT_BALANCE') {
        haptics.notify('warning');
        setConfirming(false);
        setInsufficientBalance(true);
        return;
      }
      haptics.notify('error');
      setError(apiErrorMessage(cause, 'Purchase failed. Please try again.'));
      setConfirming(false);
    } finally {
      submitting.current = false;
    }
  };

  return (
    <AnimatePresence>
      {product ? (
        <>
          <motion.div
            className={styles.scrim}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.section
            className={styles.sheet}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120) onClose();
            }}
          >
            <div className={styles.grabber} />
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <X size={18} weight="bold" />
            </button>

            <div className={styles.scroll} ref={scroll}>
              {purchased ? (
                <div className={styles.receipt}>
                  <p className={styles.receiptTitle}>
                    {purchased.activation
                      ? '✅ Your number is ready'
                      : purchased.providerProcessing
                        ? '✅ Order processed'
                      : purchased.awaitingDelivery
                        ? '✅ Payment received'
                        : '✅ Purchase complete'}
                  </p>
                  <p className={styles.receiptName}>{purchased.productName}</p>

                  {/* A rented number is issued instantly and is the whole
                      point of the purchase, so it is shown here rather than
                      leaving the buyer to find it in Orders. It also sets
                      `awaitingDelivery`, so this branch has to come first —
                      otherwise the buyer is told their number is "being
                      prepared by hand" while holding it. */}
                  {purchased.activation ? (
                    <NumberReceipt
                      phoneNumber={purchased.activation.phoneNumber}
                      expiresAt={purchased.activation.expiresAt}
                      onCopy={copyItem}
                    />
                  ) : /* Some products are prepared for each customer by hand, so
                      there is nothing to show yet. That is not the same as a
                      delivery having gone wrong, and must not read like one. */
                  purchased.awaitingDelivery ? (
                    <p className={styles.receiptPending}>
                      {purchased.providerProcessing
                        ? '⏳ Your order has been processed and will be delivered as soon as it is ready. Track it from your Orders page.'
                        : '⏳ This one is prepared by hand. It will arrive in Orders and in your chat shortly.'}
                    </p>
                  ) : purchased.deliveredItems.length > 0 ? (
                    <>
                      {purchased.deliveredItems.map((item, index) => {
                        const text = itemToText(item);
                        return (
                          <pre
                            key={index}
                            className={styles.receiptItem}
                            onClick={() => void copyItem(text)}
                          >
                            {text}
                          </pre>
                        );
                      })}
                      <p className={styles.receiptHint}>Tap to copy · also saved in Orders</p>
                    </>
                  ) : (
                    <p className={styles.receiptPending}>
                      Delivery is taking longer than usual. It will appear in Orders —{' '}
                      <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
                        contact support
                      </a>{' '}
                      if it does not arrive.
                    </p>
                  )}

                  {purchased.instructions ? (
                    <div className={styles.receiptSteps}>
                      <p className={styles.receiptStepsTitle}>📖 How to use it</p>
                      <p className={styles.receiptStepsBody}>{purchased.instructions}</p>
                    </div>
                  ) : null}
                </div>
              ) : confirming ? (
                <div className={styles.agreement}>
                  <p className={styles.agreementEyebrow}>Review before purchase</p>
                  <h2 className={styles.agreementName}>{product.name}</h2>
                  {/* Carries the discount over from the product view: this is
                      the screen the decision is made on, so dropping it here
                      would hide the saving at exactly the wrong moment. */}
                  <p className={styles.agreementPrice}>
                    {product.listPrice ? (
                      <span className={styles.wasPrice}>{product.listPrice.label}</span>
                    ) : null}
                    {product.price.label}
                  </p>
                  {product.exclusive ? <p className={styles.exclusiveBadge}>EXCLUSIVE · {product.discountLabel ?? 'LIMITED OFFER'}</p> : null}
                  {product.value ? <p className={styles.value}>Value: {product.value.label}</p> : null}
                  {!product.exclusive && product.discountLabel ? (
                    <p className={styles.discountLine}>🏷 {product.discountLabel}</p>
                  ) : null}
                  {product.flashDeal && !product.exclusive ? (
                    <div className={styles.dealTimer}>
                      <span>Flash price ends in</span>
                      <DealCountdown
                        endsAt={product.flashDeal.endsAt}
                        clockOffsetMs={clockOffsetMs}
                      />
                    </div>
                  ) : null}

                  {/* A rented number starts counting down the moment it is
                      bought, so the one thing worth saying before paying is
                      "be ready". Someone who buys one and comes back in an
                      hour has bought nothing — they are refunded, but they
                      have still wasted the trip. */}
                  {product.isNumber ? (
                    <p className={styles.numberNotice}>
                      📱 You get a phone number to receive one verification code. It stays active
                      for about 20 minutes, so start the sign-up first. No code, no charge — you
                      are refunded automatically.
                    </p>
                  ) : null}

                  {/* Some products cannot be fulfilled without something from
                      the buyer. Asked for here rather than after paying,
                      because an order that arrives without it is one the
                      operator has to chase the customer for. */}
                  {product.input ? (
                    <div className={styles.inputField}>
                      <label className={styles.inputLabel} htmlFor="product-input">
                        {product.input.placeholder ?? 'We need one more detail'}
                      </label>
                      <input
                        id="product-input"
                        className={styles.input}
                        type={product.input.type === 'EMAIL' ? 'email' : 'text'}
                        inputMode={
                          product.input.type === 'NUMBER'
                            ? 'tel'
                            : product.input.type === 'EMAIL'
                              ? 'email'
                              : 'text'
                        }
                        placeholder={product.input.placeholder ?? ''}
                        value={answer}
                        autoComplete={product.input.type === 'EMAIL' ? 'email' : 'off'}
                        onChange={(event) => setAnswer(event.target.value)}
                      />
                      <p className={styles.inputHint}>
                        This is sent to us with your order so we can complete it.
                      </p>
                    </div>
                  ) : null}

                  {/* Only asked for when there is something to read. Most of
                      the catalogue has no description yet, and a tickbox
                      confirming you have read "no information was provided"
                      is friction that informs nobody — those products get the
                      plain confirmation the shop has always had, and gain the
                      agreement automatically once their copy is written. */}
                  {product.details ? (
                    <>
                      <div className={styles.agreementDetailsCard}>
                        <p className={styles.agreementDetailsTitle}>Full product information</p>
                        <p className={styles.agreementDetails}>{product.details}</p>
                      </div>

                      <label className={styles.agreementCheck}>
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(event) => {
                            setAgreed(event.target.checked);
                            haptics.select();
                          }}
                        />
                        <span className={styles.checkbox} aria-hidden="true" />
                        <span>
                          I have read and agree to the product information, delivery conditions,
                          warranty and limitations shown above.
                        </span>
                      </label>
                    </>
                  ) : null}
                </div>
              ) : (
                <>
                  <ProductLogo
                    className={styles.logo}
                    src={product.logoUrl}
                    width={72}
                    height={72}
                    iconSize={32}
                  />
                  <h2 className={styles.name}>{title ?? product.name}</h2>

                  {hasVariants ? (
                    <fieldset className={styles.variantPicker}>
                      <legend className={styles.variantLegend}>Choose an option</legend>
                      <div className={styles.variantGrid}>
                        {variants.map((variant) => {
                          const selected = variant.product.id === product.id;
                          return (
                            <button
                              key={variant.product.id}
                              type="button"
                              className={selected ? styles.variantActive : styles.variant}
                              aria-pressed={selected}
                              onClick={() => {
                                haptics.select();
                                onVariantSelect?.(variant.product.id);
                              }}
                            >
                              <span className={styles.variantLabel}>{variant.label}</span>
                              <span className={styles.variantPrice}>
                                {variant.product.price.label}
                              </span>
                              <span
                                className={
                                  variant.product.inStock
                                    ? styles.variantStock
                                    : styles.variantStockOut
                                }
                              >
                                {variant.product.inStock ? 'Available' : 'Out of stock'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>
                  ) : null}
                  <p className={styles.price}>
                    {product.listPrice ? (
                      <span className={styles.wasPrice}>{product.listPrice.label}</span>
                    ) : null}
                    {product.price.label}
                  </p>
                  {product.exclusive ? <p className={styles.exclusiveBadge}>EXCLUSIVE · {product.discountLabel ?? 'LIMITED OFFER'}</p> : null}
                  {product.value ? <p className={styles.value}>Value: {product.value.label}</p> : null}
                  {!product.exclusive && product.discountLabel ? (
                    <p className={styles.discountLine}>🏷 {product.discountLabel}</p>
                  ) : null}
                  {product.flashDeal && !product.exclusive ? (
                    <div className={styles.dealTimer}>
                      <span>Flash price ends in</span>
                      <DealCountdown
                        endsAt={product.flashDeal.endsAt}
                        clockOffsetMs={clockOffsetMs}
                      />
                    </div>
                  ) : null}
                  <p className={styles.stock}>{stockLabel(product)}</p>

                  <AnimatePresence initial={false}>
                    {showInstructions && product.details ? (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className={styles.details}>{product.details}</p>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </>
              )}
            </div>

            <footer className={styles.footer}>
              {error ? <p className={styles.error}>{error}</p> : null}

              {purchased ? (
                <Button
                  fullWidth
                  onClick={() => {
                    if (purchased.providerProcessing) {
                      onClose();
                      navigate('/orders');
                      return;
                    }
                    onClose();
                  }}
                >
                  {purchased.providerProcessing ? 'Track order' : 'Done'}
                </Button>
              ) : confirming && product.inStock ? (
                <div className={styles.confirmRow}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setConfirming(false);
                      setAgreed(false);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    fullWidth
                    loading={isLoading}
                    // Withheld until the buyer has both agreed to whatever
                    // there was to read and answered whatever was asked.
                    disabled={
                      (Boolean(product.details) && !agreed) ||
                      Boolean(product.input && answer.trim() === '')
                    }
                    onClick={() => void buy()}
                  >
                    {product.details ? 'Purchase now' : `Confirm ${product.price.label}`}
                  </Button>
                </div>
              ) : (
                <div className={styles.buyRow}>
                  {!product.inStock ? (
                    <Button fullWidth disabled>
                      Out of stock
                    </Button>
                  ) : (
                    <Button fullWidth onClick={beginPurchaseReview}>
                      Buy for {product.price.label}
                    </Button>
                  )}

                  {/* Narrow, to the right of Buy: the instructions matter, but
                      not enough to push the purchase off the first screen.
                      It remains available even when the customer cannot buy:
                      balance and stock control purchasing, not whether product
                      information may be read. */}
                  {product.details ? (
                    <Button
                      variant={showInstructions ? 'primary' : 'secondary'}
                      className={styles.infoButton}
                      aria-label={showInstructions ? 'Hide instructions' : 'Show instructions'}
                      onClick={() => setShowInstructions((current) => !current)}
                    >
                      <Info size={18} weight={showInstructions ? 'fill' : 'regular'} />
                      <span className={styles.infoLabel}>Info</span>
                    </Button>
                  ) : null}
                </div>
              )}
            </footer>
          </motion.section>

          <InsufficientBalanceSheet
            // A receipt is proof that this attempt was paid. Keep the two
            // terminal UI states mutually exclusive even if another wallet
            // refresh lands while the receipt is open.
            open={insufficientBalance && !purchased}
            source="product"
            onClose={() => setInsufficientBalance(false)}
          />
        </>
      ) : null}
    </AnimatePresence>
  );
}
